import { useState, useEffect } from 'react';
import { useSupplierFilterOptions } from '../../hooks/useReferenceData';

interface SupplierFiltersProps {
  initialValues?: SupplierFilterValues;
  onFiltersReady?: (getFilters: () => SupplierFilterValues) => void;
}

export interface SupplierFilterValues {
  supplierName: string;
  regionId: string;
  countryId: string;
}

const defaultFilters: SupplierFilterValues = {
  supplierName: '',
  regionId: '',
  countryId: ''
};

interface Region {
  id: number;
  name: string;
  countryId: number;
}

interface Country {
  id: number;
  name: string;
}

export const SupplierFilters = ({ initialValues, onFiltersReady }: SupplierFiltersProps): JSX.Element => {
  const [filters, setFilters] = useState<SupplierFilterValues>(initialValues || defaultFilters);
  const { data: filterOptions } = useSupplierFilterOptions();
  const regions = (filterOptions?.regions || []) as Region[];
  const countries = (filterOptions?.countries || []) as Country[];

  // Update local state when initialValues change
  useEffect(() => {
    setFilters(initialValues ?? defaultFilters);
  }, [initialValues]);

  // Provide a way for parent to get current filter values
  useEffect(() => {
    if (onFiltersReady) {
      onFiltersReady(() => filters);
    }
  }, [filters, onFiltersReady]);

  const handleChange = (field: keyof SupplierFilterValues, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Filter regions based on selected country - show all if no country selected
  const filteredRegions = filters.countryId
    ? regions.filter(r => r.countryId.toString() === filters.countryId)
    : regions;

  return (
    <div className="w-full" style={{ paddingTop: '24px' }}>
      <h2 style={{
        fontFamily: 'Oxygen',
        fontSize: '24px',
        fontWeight: 700,
        color: '#101828',
        marginBottom: '32px'
      }}>
        Supplier
      </h2>

      <div className="space-y-6">
        {/* Supplier Name */}
        <div>
          <label style={{
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 700,
            color: '#344054',
            display: 'block',
            marginBottom: '6px'
          }}>
            Supplier Name
          </label>
          <input
            type="text"
            placeholder="Search supplier name"
            value={filters.supplierName}
            onChange={(e) => handleChange('supplierName', e.target.value)}
            style={{
              width: '100%',
              height: '40px',
              padding: '10px 14px',
              border: '1px solid #D0D5DD',
              borderRadius: '6px',
              fontFamily: 'Oxygen',
              fontSize: '14px',
              color: '#101828'
            }}
          />
        </div>

        {/* Country */}
        <div>
          <label style={{
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 700,
            color: '#344054',
            display: 'block',
            marginBottom: '6px'
          }}>
            Country
          </label>
          <select
            name="country"
            value={filters.countryId}
            onChange={(e) => handleChange('countryId', e.target.value)}
            style={{
              width: '100%',
              height: '40px',
              padding: '10px 14px',
              border: '1px solid #D0D5DD',
              borderRadius: '6px',
              fontFamily: 'Oxygen',
              fontSize: '14px',
              color: '#101828',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Countries</option>
            {countries.map(country => (
              <option key={country.id} value={country.id}>{country.name}</option>
            ))}
          </select>
        </div>

        {/* Region */}
        <div>
          <label style={{
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 700,
            color: '#344054',
            display: 'block',
            marginBottom: '6px'
          }}>
            Region
          </label>
          <select
            value={filters.regionId}
            onChange={(e) => handleChange('regionId', e.target.value)}
            style={{
              width: '100%',
              height: '40px',
              padding: '10px 14px',
              border: '1px solid #D0D5DD',
              borderRadius: '6px',
              fontFamily: 'Oxygen',
              fontSize: '14px',
              color: '#101828',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Regions</option>
            {filteredRegions.map(region => (
              <option key={region.id} value={region.id}>{region.name}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};
